
import { empty as observableEmpty,
         throwError as observableThrowError,
         BehaviorSubject,
         Observable } from 'rxjs'

import { map,
         catchError,
         distinctUntilChanged,
         filter } from 'rxjs/operators'

// from thinkster realworld
import { Injectable,
         isDevMode } from '@angular/core'

import { HttpClient,
         HttpErrorResponse,
         HttpEvent,
         HttpEventType,
         HttpHeaders,
         HttpParams,
         HttpRequest } from '@angular/common/http'

import { Router,
         RouterState } from '@angular/router'

import { environment } from '../../../environments/environment'

import { retryWithBackoff } from '../_operators/retry-with-backoff'

// import { FileItem,
//          FileItemUploadProgress,
//          FileItemShrineMetadata,
//          FileItemShrineData,
//          FileItemUploadResponse } from '../_models/file-item.model'

import { CookieService } from './cookie.service'
// import { ToastService } from './toast.service'
import { RouterService } from './router.service'
import { AppStatic } from '../_helpers/app-static'

// require('dotenv').config();



@Injectable()
export class ApiService {

  private shouldCheckCurrentUserSubject = new BehaviorSubject<boolean>(false)
  public shouldCheckCurrentUser = this.shouldCheckCurrentUserSubject.asObservable()

  private returnPathSubject = new BehaviorSubject<string>('home')
  public returnPath = this.returnPathSubject.asObservable().pipe(distinctUntilChanged())
  public returnPathLocal: string = 'home'

  public shouldLogOut = false

  isDevMode = isDevMode()
  hideToast5XX = false
  lastLoginLoad = 0

  constructor(
    private cookieService: CookieService,
    private httpClient: HttpClient,
    private router: Router,
    private routerService: RouterService,
    // private toast: ToastService
  ) {}

  get(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    let p = this.pathWithSlash(path)
    let options = {
      headers: this.setHeaders(),
      params: params
    }

    let req = new HttpRequest('GET', `${environment.api_url}${p}`, options)
    return this.httpClient.request(req).pipe(
      retryWithBackoff(),
      filter(event => {
        return this.checkEventType(event.type, HttpEventType.Response)
      }),
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Response:
            return this.extractData(event)
          default: {
            console.warn("api.service get - INVALID RESPONSE", event)
            break
          }
        }
      }),
      catchError((e: HttpErrorResponse) => {
        // console.log('catchError', e)
        return this.handleHttpError(e)
      })
    )

  }

  put(path: string, body = {}): Observable<any> {
    let p = this.pathWithSlash(path)
    let options = { headers: this.setHeaders() }
    let req = new HttpRequest('PUT', `${environment.api_url}${p}`, body, options)

    return this.httpClient.request(req).pipe(
      filter(event => this.checkEventType(event.type, HttpEventType.Response)),
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Response:
            return this.extractData(event)
          default:
            break
        }
      }),
      catchError((e: HttpErrorResponse) => {
        return this.handleHttpError(e)
      })
     )
  }

  post(path: string, body = {}, additionalOptions = {}): Observable<any> {
    let p = this.pathWithSlash(path)
    let options = { headers: this.setHeaders() }
    let req = new HttpRequest('POST', `${environment.api_url}${p}`, body, options)

    // console.log('POST', req)

    return this.httpClient.request(req).pipe(
      filter(event => this.checkEventType(event.type, HttpEventType.Response)),
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Response:
            return this.extractData(event)
          default:
            console.warn("api.service post - INVALID RESPONSE", event)
            break
        }
      }),
      catchError((e: HttpErrorResponse) => {
        return this.handleHttpError(e)
      })
    )
  }

  delete(path: string): Observable<any> {
    let p = this.pathWithSlash(path)
    let options = { headers: this.setHeaders() }
    let req = new HttpRequest('DELETE', `${environment.api_url}${p}`, options)
    // let deleteEventTypes = [ HttpEventType.UploadProgress,
    //                          HttpEventType.Response ]

    return this.httpClient.request(req).pipe(
      // .filter(event => this.checkEventType(event.type, deleteEventTypes))
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Sent:
            console.log('api.delete - sent', event)
            return this.extractData(event)
          case HttpEventType.Response:
            return this.extractData(event)
          default:
            console.warn("api.service delete - NO RESPONSE", event)
            return true
        }
      }),
      catchError((e: HttpErrorResponse) => {
        return this.handleHttpError(e)
      })
    )
  }

  // checks event is one of enumerated types
  checkEventType(eventType:number, types: number|number[]) {
    // console.log('checkEventType', eventType, types)
    if (Array.isArray(types)) {
      return types.indexOf(eventType) >= 0
    } else {
      return eventType === types
    }
  }

  updateLastLoginLoad() {
    this.lastLoginLoad = Date.now()
  }

  // delay is time (ms) elapsed before checking and executingn redirect
  navigateToLoginPageOnce(delay: number) {
    // console.log('nlpOnce', delay,  window)
    setTimeout(this.navigateToLoginPageUnlessIntercepted.bind(this, delay), delay)
  }

  navigateToLoginPageUnlessIntercepted(delay: number, params={}): Promise<any> {

    let now = Date.now()
    // console.log('navToLogin', this.lastLoginLoad, (now - delay))
    if (this.lastLoginLoad > (now - delay)) return Promise.resolve()
    return this.navigateToLoginPage()
  }

  navigateToLoginPage(params = {}): Promise<any> {
    // console.log('nlp params', params, this.router.url)
    // this.updateReturnUrlFromParams(params)
    // redirect to login after delay
    let delayMs = 1000 
    let qp = {}
    let returnPath = params['returnPath']
    if (returnPath) qp = Object.assign(qp, { redir: returnPath } )
    return new Promise(resolve => setTimeout(resolve, delayMs)).then(res => {
      // console.log('NAVIGATING TO LOGIN', this.returnPathLocal, returnPath)
      return this.router.navigate(['/login'], { queryParams: qp })
    })
  }

  updateShouldCheckCurrentUser(should: boolean) {
    this.shouldCheckCurrentUserSubject.next(should)
  }

  setHeaders(headerType = 'default'): HttpHeaders {
    // TODO: share token with backend, as ENV variable
    let token = this.cookieService.get('local', 'lcp_access_token') // process.env.JWT_ACCESS_KEY)


    let httpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${token}`)
    
      // console.log('SETTING HEADERS', httpHeaders)

    if (headerType !== 'upload') {
      // angular bug - mustn't set Content-Type for multipart/form-data post:
      // https://github.com/angular/angular/issues/11819
      httpHeaders = httpHeaders.set('Content-Type', 'application/json')
    }
    return httpHeaders
  }

  extractData(res: any) {
    return res.body
  }

  handleHttpError(httpError: HttpErrorResponse) {
    if (!httpError.status) return this.returnHttpError(httpError)

    if (httpError.status === 401) {
      // UNAUTHORIZED error
      // if (!httpError.error) return this.returnHttpError(httpError)
      if (httpError.error['recaptcha_error'] || httpError.error['pundit_error']) {
        this.updateShouldCheckCurrentUser(true)
        return this.returnHttpError(httpError)
      }

      if (httpError.error['token_error']) {
        // console.log("TOKEN - redirecting to login page", this.router.url)
        // this.toast.warning('You must be logged in', 'Not Permitted')
        this.updateShouldCheckCurrentUser(true)
        // this.navigateToLoginPage()
        this.navigateToLoginPageOnce(1000)
        return this.returnHttpError(httpError)
        // return this.navigateToLoginPopup() // { closeModal: true })
      }
    }

    if (httpError.status === 404) {

    }

    if (httpError.status === 422) {
      if (httpError.error['already_exists']) {
        console.log('httpError', httpError)
      }

      return this.returnHttpError(httpError)
    }

    if (499 < httpError.status && httpError.status < 512) {
      if (this.hideToast5XX) return Promise.reject()
      this.showToast5XX(httpError.status)
      this.persistHideToast()
      // if (this.isDevMode) return // this.toast.warning('(dev) rollbar prevented', httpError.status)
      if (this.isDevMode) {
        // // this.toast.warning('(dev) rollbar', httpError.status, // this.toast.persistedOptions())
        // this.toast.warning('(dev) rollbar', httpError.status)
        return this.returnHttpError(httpError)
      }
      // return Observable.of(e.error)
      return Promise.reject()
    }

    if (this.isDevMode) {
      console.warn("API HTTP ERROR:", httpError)
      console.log('ERR:', httpError.error.message)
    }
    return this.returnHttpError(httpError)
  }

  returnHttpError(e: HttpErrorResponse): Observable<HttpErrorResponse> {
    // if (this.isDevMode) {
    //   console.log('ERROR', e.error)
    //   return observableEmpty()
    // }
    return observableThrowError(e.error)
  }

  pathWithSlash(path: any) {
    if (!path) return
    if (path.charAt(0) !== "/") { path = "/" + path }
    return path
  }

  showToast5XX(errorStatus: number) {
    let msg = `Something went wrong... Please try again after refreshing the page. (${errorStatus})`
    // // this.toast.error(msg, "Oops...", // this.toast.persistedOptions())
    // this.toast.error(msg, "Oops...")
  }

  persistHideToast(duration = 5000) {
    this.setHideToast5XXNow(true)

    setTimeout(this.setHideToast5XXNow.bind(this, false), duration)
  }

  setHideToast5XXNow(should:boolean) {
    this.hideToast5XX = should
  }
}
